<template>
  <div class="disb-subsection">
    <div class="d-flex justify-content-between align-items-center flex-wrap">
      <b-form-input
        v-if="editableTitle"
        :value="title"
        @input="v => $emit('update:title', v)"
        placeholder="Group name (e.g. Lawyer Fee)"
        class="cheque-group-title-input"
      />
      <div v-else class="disb-subtitle mb-0">{{ title }}</div>

      <div class="d-flex align-items-center" style="gap: 12px;">
        <b-form-checkbox
          v-if="mode === 'remainder-last-cheque'"
          switch
          :checked="manualLast"
          @change="v => $emit('update:manualLast', v)"
        >
          Manual last amount
        </b-form-checkbox>
        <b-button
          v-if="removable"
          variant="flat-danger"
          size="sm"
          class="custom-row-remove"
          @click="$emit('remove-group')"
        >
          <feather-icon icon="Trash2Icon" size="14" />
        </b-button>
      </div>
    </div>

    <div v-for="(cheque, index) in cheques" :key="'cheque-' + index" class="custom-row mt-1">
      <b-form-input
        :value="cheque.name"
        @input="v => updateCheque(index, { name: v })"
        placeholder="Payee / cheque name"
        class="custom-row-label"
      />
      <b-form-input
        :value="cheque.remark"
        @input="v => updateCheque(index, { remark: v })"
        placeholder="Remark / detail"
        class="custom-row-remark"
      />
      <b-input-group prepend="฿" class="custom-row-amount">
        <b-form-input
          v-if="isLastLocked(index)"
          :value="formatNumber(remainderForLast)"
          type="text"
          disabled
        />
        <b-form-input
          v-else
          :value="formatNumber(cheque.amount)"
          type="text"
          placeholder="0"
          @input="v => updateCheque(index, { amount: parseNumber(v) })"
        />
      </b-input-group>
      <b-button
        v-if="allowMultiple"
        variant="flat-danger"
        class="custom-row-remove"
        @click="removeCheque(index)"
      >
        <feather-icon icon="XIcon" size="16" />
      </b-button>
    </div>

    <b-button v-if="allowMultiple" variant="outline-secondary" size="sm" class="mt-50" @click="addCheque">
      <feather-icon icon="PlusIcon" size="13" class="mr-25" />Add Cheque
    </b-button>

    <small v-if="mode === 'remainder-last-cheque'" class="text-muted d-block mt-25">
      <template v-if="manualLast">
        Every cheque's amount is editable — the last row no longer auto-fills, so type it in yourself
        and keep the total in line with "{{ targetLabel }}".
      </template>
      <template v-else>
        Each cheque's amount is editable, except the last, which auto-fills with whatever remains of
        "{{ targetLabel }}" — so the total can never exceed it. Toggle "Manual last amount" to enter it
        by hand instead.
      </template>
    </small>
    <small v-else-if="mode === 'remainder-cash' && allowMultiple" class="text-muted d-block mt-25">
      Add one row per cashier cheque. Whatever isn't covered by cheques is assumed paid in cash.
    </small>
    <small v-else-if="mode === 'remainder-cash'" class="text-muted d-block mt-25">
      Whatever isn't covered by the cheque is assumed paid in cash.
    </small>

    <div v-if="mode === 'remainder-last-cheque' && !totalsMatch" class="text-warning small mt-50">
      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
      Total Cashier Cheque (฿ {{ formatNumber(chequeTotal) }}) doesn't match {{ targetLabel }}
      (฿ {{ formatNumber(target) }}).
    </div>
    <div v-if="mode === 'remainder-cash' && cashAmount < 0" class="text-warning small mt-50">
      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
      Cashier cheques total ฿ {{ formatNumber(chequeTotal) }}, which is ฿ {{ formatNumber(-cashAmount) }}
      more than {{ targetLabel }}.
    </div>

    <div class="disb-summary-line mt-50">
      <span>Total Cashier Cheque</span>
      <strong>฿ {{ formatNumber(chequeTotal) }}</strong>
    </div>
    <template v-if="mode === 'remainder-cash'">
      <div class="disb-summary-line">
        <span>Cash</span>
        <strong>฿ {{ formatNumber(cashAmount) }}</strong>
      </div>
      <div class="disb-summary-line disb-summary-line--total">
        <span>{{ targetLabel }}</span>
        <strong>฿ {{ formatNumber(target) }}</strong>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BFormInput, BInputGroup, BButton, BFormCheckbox,
} from 'bootstrap-vue'
import { formatNumber, parseNumber } from '../format'
import {
  modeForKind, chequeRemainder, chequeGroupTotal, isLastLocked as isLastLockedHelper,
} from '../chequeGroups'

export default {
  components: {
    BFormInput, BInputGroup, BButton, BFormCheckbox,
  },
  props: {
    title: { type: String, default: '' },
    editableTitle: { type: Boolean, default: false },
    removable: { type: Boolean, default: false },
    kind: { type: String, required: true },
    cheques: { type: Array, required: true },
    target: { type: Number, default: 0 },
    targetLabel: { type: String, default: '' },
    manualLast: { type: Boolean, default: false },
  },
  computed: {
    mode() {
      return modeForKind(this.kind)
    },
    allowMultiple() {
      return this.kind !== 'government'
    },
    pseudoGroup() {
      return { kind: this.kind, cheques: this.cheques, manualLast: this.manualLast }
    },
    remainderForLast() {
      return chequeRemainder(this.pseudoGroup, this.target)
    },
    chequeTotal() {
      return chequeGroupTotal(this.pseudoGroup, this.target)
    },
    cashAmount() {
      return this.target - this.chequeTotal
    },
    totalsMatch() {
      return Math.round(this.chequeTotal * 100) === Math.round(this.target * 100)
    },
  },
  methods: {
    formatNumber,
    parseNumber,
    isLastLocked(index) {
      return isLastLockedHelper(this.pseudoGroup, index)
    },
    updateCheque(index, patch) {
      const updated = this.cheques.map((c, i) => (i === index ? { ...c, ...patch } : c))
      this.$emit('update:cheques', updated)
    },
    addCheque() {
      const lastIndex = this.cheques.length - 1
      const updated = lastIndex < 0 ? [] : this.cheques.map((c, i) => (
        i === lastIndex && this.isLastLocked(i)
          ? { ...c, amount: this.remainderForLast }
          : c
      ))
      updated.push({ name: '', remark: '', amount: null })
      this.$emit('update:cheques', updated)
      if (this.mode === 'remainder-last-cheque' && this.manualLast) this.$emit('update:manualLast', false)
    },
    removeCheque(index) {
      const wasLastLocked = this.isLastLocked(index)
      const updated = this.cheques.filter((_, i) => i !== index)
      this.$emit('update:cheques', updated)
      if (wasLastLocked && this.manualLast) this.$emit('update:manualLast', false)
    },
  },
}
</script>
